import {
  collectAdRefFilenames,
  mapAdRefUrls,
  resolveAdRefs,
} from "@hightouch/lib/ad-studio/html-editor/resolve-ad-refs";
import {
  createZipBlob,
  deduplicateFilenames,
  fetchUrlAsBlob,
  sanitizeFilename,
} from "src/utils/download";

type ZipEntry = { filename: string; blob: Blob };

type WebPageArgs = {
  html: string;
  assets: Record<string, string>;
  apiBase: string;
};

/** A web page zipped as a standalone folder; see `buildWebPageEntries`. */
export async function buildWebPageZip(
  args: WebPageArgs,
): Promise<{ blob: Blob; missing: string[] }> {
  const { entries, missing } = await buildWebPageEntries(args);
  return { blob: await createZipBlob(entries), missing };
}

/**
 * A web page's files for a standalone folder: `index.html` with its `ad:` images
 * pointed at `images/`. `assets` maps each ref's filename to a signed URL.
 */
export async function buildWebPageEntries({
  html,
  assets,
  apiBase,
}: WebPageArgs): Promise<{ entries: ZipEntry[]; missing: string[] }> {
  const doc = new DOMParser().parseFromString(html, "text/html");
  // A saved page loads public asset URLs, which `assets` lists under the filename their ad: ref would have.
  const publicRefs = new Map(
    Object.entries(assets).map(([filename, url]) => [url, `ad:${filename}`]),
  );
  mapAdRefUrls(doc.documentElement, (url) => publicRefs.get(url) ?? url);
  // Sanitizing can map distinct filenames to one path, so dedupe before rewriting refs.
  const imagePaths: Record<string, string> = Object.fromEntries(
    deduplicateFilenames(
      [...collectAdRefFilenames(doc.documentElement.outerHTML)].map((ref) => ({
        ref,
        filename: `images/${sanitizeFilename(ref)}`,
      })),
    ).map(({ ref, filename }) => [ref, filename]),
  );

  // URLs stand in for S3 keys: resolveAdRefs only resolves refs that have a key.
  resolveAdRefs(doc.documentElement, assets, apiBase, {
    targetRenderer: "hightouch-webapp",
    directUrls: imagePaths,
  });
  // Drops the refs resolveAdRefs records for the editor's save path.
  for (const el of Array.from(doc.querySelectorAll("*"))) {
    for (const attr of Array.from(el.attributes)) {
      if (attr.name.startsWith("data-ad-ref")) el.removeAttribute(attr.name);
    }
  }

  const apiOrigin = new URL(apiBase).origin;
  const missing: string[] = [];
  const images = await Promise.all(
    Object.entries(imagePaths).map(
      async ([filename, path]): Promise<ZipEntry | null> => {
        const url = Object.hasOwn(assets, filename)
          ? assets[filename]
          : undefined;
        // API-served assets (e.g. SVGs) need the session cookie; signed URLs must not get it.
        const credentials =
          url && URL.canParse(url) && new URL(url).origin === apiOrigin
            ? "include"
            : undefined;
        const blob = url ? await fetchUrlAsBlob(url, credentials) : null;
        if (!blob) {
          missing.push(filename);
          return null;
        }
        return { filename: path, blob };
      },
    ),
  );

  const entries: ZipEntry[] = [
    {
      filename: "index.html",
      blob: new Blob([`<!DOCTYPE html>${doc.documentElement.outerHTML}`], {
        type: "text/html",
      }),
    },
    ...images.filter((image): image is ZipEntry => image !== null),
  ];
  return { entries, missing };
}
